import { createApiUrl } from '../api'
import type { UserOrderItemResponse } from '../../types/types.bids'

export interface CheckoutSessionResponse {
  paymentId: string
  sessionId: string
  checkoutUrl: string
}

export interface ShippingEstimateRequest {
  name?: string
  street?: string
  city?: string
  state?: string
  postalCode: string
  country?: string
  phone?: string
  email?: string
}

export interface ShippingRateOption {
  rateId: string
  shipmentId: string
  provider: string
  serviceLevelName: string
  serviceLevelToken: string | null
  amount: number
  currency: string
  estimatedDays: number | null
  durationTerms: string | null
}

export interface ShippingEstimateResponse {
  order: UserOrderItemResponse
  selectedRate: ShippingRateOption
  rates: ShippingRateOption[]
}

export interface ListingShippingEstimateResponse {
  selectedRate: ShippingRateOption
  rates: ShippingRateOption[]
  originSummary: string
  destinationSummary: string
}

export class OrderAPI {
  /**
   * Get user's all orders
   */
  static async getUserAllOrders(accessToken: string): Promise<UserOrderItemResponse[]> {
    const url = createApiUrl('/api/v1/marketplace/payments/user/orders/all')

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`,
      },
    })

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Unauthorized')
      }
      throw new Error(`Failed to fetch all orders: ${response.statusText}`)
    }

    return response.json()
  }

  /**
   * Get user's unpaid orders
   */
  static async getUserUnpaidOrders(accessToken: string): Promise<UserOrderItemResponse[]> {
    const url = createApiUrl('/api/v1/marketplace/payments/user/orders/unpaid')

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`,
      },
    })

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Unauthorized')
      }
      throw new Error(`Failed to fetch unpaid orders: ${response.statusText}`)
    }

    return response.json()
  }

  /**
   * Get user's paid orders
   */
  static async getUserPaidOrders(accessToken: string): Promise<UserOrderItemResponse[]> {
    const url = createApiUrl('/api/v1/marketplace/payments/user/orders/paid')

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`,
      },
    })

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Unauthorized')
      }
      throw new Error(`Failed to fetch paid orders: ${response.statusText}`)
    }

    return response.json()
  }

  /**
   * Create a hosted Stripe Checkout session for a specific unpaid order.
   */
  static async createCheckoutSession(
    paymentId: string,
    accessToken: string
  ): Promise<CheckoutSessionResponse> {
    const url = createApiUrl(`/api/v1/marketplace/payments/${paymentId}/checkout-session`)

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`,
      },
    })

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Unauthorized')
      }
      if (response.status === 403) {
        throw new Error('You do not have access to this checkout session')
      }
      if (response.status === 404) {
        throw new Error('Checkout session not found')
      }
      if (response.status === 400) {
        throw new Error('This order is no longer available for payment')
      }
      throw new Error(`Failed to create checkout session: ${response.statusText}`)
    }

    return response.json()
  }

  /**
   * Confirm a returned Stripe Checkout session and load the updated order.
   */
  static async confirmCheckoutSession(
    paymentId: string,
    checkoutSessionId: string,
    accessToken: string
  ): Promise<UserOrderItemResponse> {
    const params = new URLSearchParams({ session_id: checkoutSessionId })
    const url = createApiUrl(`/api/v1/marketplace/payments/${paymentId}/checkout-session/confirm?${params.toString()}`)

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`,
      },
    })

    if (!response.ok) {
      const error = await response.text()
      throw new Error(error || `Failed to confirm checkout session: ${response.statusText}`)
    }

    return response.json()
  }

  /**
   * Estimate checkout shipping and apply the selected lowest rate to the order total.
   */
  static async estimateShipping(
    paymentId: string,
    shippingAddress: ShippingEstimateRequest,
    accessToken: string
  ): Promise<ShippingEstimateResponse> {
    const url = createApiUrl(`/api/v1/marketplace/payments/${paymentId}/shipping/estimate`)

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`,
      },
      body: JSON.stringify(shippingAddress),
    })

    if (!response.ok) {
      const error = await response.text()
      throw new Error(error || `Failed to estimate shipping: ${response.statusText}`)
    }

    return response.json()
  }

  /**
   * Estimate pre-bid shipping for a live listing using the user's default shipping address.
   */
  static async estimateListingShipping(
    listingId: string,
    bidAmount: number,
    accessToken: string
  ): Promise<ListingShippingEstimateResponse> {
    const params = new URLSearchParams({ bidAmount: bidAmount.toFixed(2) })
    const url = createApiUrl(`/api/v1/listings/${listingId}/shipping/estimate?${params.toString()}`)

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`,
      },
    })

    if (!response.ok) {
      const error = await response.text()
      throw new Error(error || `Failed to estimate listing shipping: ${response.statusText}`)
    }

    return response.json()
  }
}
